import { useCallback, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { shallowEqual, useLocation, useSelector, useIntl } from 'umi';
import { useMemoizedFn } from 'ahooks';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { castArray, compact, concat, find, isEmpty, isFunction, isObject, isString } from 'lodash-es';
import useNetworkRecovery from './useNetworkRecovery';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import { useRequestController } from '../../Home/hooks/useRequestController.ts';
import { parseJsonSafely, preprocessMarkdown, processData, transformMessages } from '../utils';
import { getAuthorization } from '@/utils/utils';
import { useLocalState, useLocalRef, store } from './useLocalState';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { getHistoryList } from '../../service/service';
import { useGlobalReportManager } from '../../components/ReportViewer/useGlobalReportManager';
import { Conversation, TStreamUpdateData, TStreamingState, TCurrentMessageInfo } from '../types/history';
import { IStreamChunk, MessagePart } from '../types/chat';
import { ERROR_CODES, COPILOT_REPORT_SESSION_ID_KEY, CHART_DATA_TYPES, TEXT_CONCAT_DATA_TYPES } from '../../config/constant';
const useChatHistory = () => {
  const location: any = useLocation();

  const {
    state: { initFileList } = {},
    query: { sessionId, chatMode, template },
  } = location || {};
  const [sessionCopilotId, setSessionCopilotId] = useLocalState<string>(`__dba_chat_copilot__`, '');
  // 调整动机：在Agent落地页我们的SessionId是稳定的，但是Copilot的SessionId是不稳定的，（比如在各个子系统可能会发生清除url参数的行为）
  // 因此我们在这里使用全局状态sessionCopilotId来替代路由参数sessionId，以保证Copilot的SessionId的稳定性，这里兼容了2个id，一个url外显的iD，一个全局状态的id，优先使用全局状态sessionCopilotId
  const routeSessionId = sessionId || sessionCopilotId;
  const streamingStateRef = useLocalRef<TStreamingState>(`__dba_chat_history_streaming_state_${routeSessionId}__`, null);
  const streamingState = streamingStateRef.current;
  // ds 报告全局数据（按 sessionId + document_id 管理），这里读取当前激活的报告实例
  const { activeReport, updateReport, openReport } = useGlobalReportManager({ sessionId: routeSessionId });
  const dsReport = activeReport || { show: false };
  const { formatMessage, locale } = useIntl();
  const showErrorTips = useErrorTips();

  const userInfo = useSelector(
    (state: any) => state.home.userInfo,
    shallowEqual,
  );

  const [loading, setLoading] = useLocalState<boolean>(`__dba_chat_history_loading_${routeSessionId}__`, false);

  const [data, mutate] = useLocalState<Conversation[]>(`__dba_chat_history_${routeSessionId}__`, []);

  const [isDSReportSession] = useLocalState('__dba_is_ds_report_session__', false);
  const isDsReport = (template && template === 'dsReport') || isDSReportSession;

  const { runAsync, mutate: mutateGuessNext, data: guessNextData } = useRequestController({
    method: 'POST',
    manual: true,
    url: `/api/agent/latest_guessnext/v2`,
    refreshDeps: useMemo(
      () => ({
        sessionid: routeSessionId,
        trace_id: uuidv4(),
      }),
      [routeSessionId],
    ),
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
    // 动机：直接全局拿数据，无需层层传递，历史数据从这里发起
    cacheKey: `latest_guessnext${routeSessionId}`,
  });
  const fetchHistoryList = (onHistorySuccess?: (res: any) => void) => {
    // 如果没有sessionId，直接中止
    if (!routeSessionId) return;
    // 获取猜你所想历史数据
    if (isDsReport) {
      mutateGuessNext({ pending: true});
    } else {
      // 有缓存就用缓存，不在发起请求
      isEmpty(guessNextData?.result?.queries) && runAsync().then((res) => {
        mutateGuessNext({...res, pending: true});
      }).catch(() => {
        mutateGuessNext({...guessNextData, pending: true});
      });
    }
    // 前端缓存机制，如果已经加载过一次，就不需要在调接口，这里有一个请求竞速问题，如果在后端数据还没有返回，直接切走，前端代码还没有执行，没有返回answer字段，没有命中缓存，会直接调history  
    // 因为chat还没有执行完成，history肯定也是空的，此时页面一定是空白，这里加个兜底，如果已经有了msgId，就认为是开启了新对话，继续执行缓存
    if ((data?.[0]?.answer?.length || data?.[0]?.messageId)) {
      return;
    }
    setLoading(true);
    const { openid } = userInfo || {};
 
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    getHistoryList({ sessionId: routeSessionId, openid }, showErrorTips).then(async (res) => {
      try {
        onHistorySuccess?.(res);
        // 使用新的格式化函数处理历史消息
        const resData = await processData(transformMessages(res?.messages || []));
        mutate(resData);
      } catch (error) {
        console.error('处理历史数据失败:', error);
        // 即使图片处理失败，也要设置加载完成状态
      } finally {
        setLoading(false);
      }
    }).catch((error) => {
      console.error('获取历史数据失败:', error);
      setLoading(false);
    });
  };

  const setConversations = (set: Conversation[]) => {
    mutate(set);
  };
  const conversations: Conversation[] = useMemo(() => {
    if (loading) return [];

    // 处理单个回答项
    // TODO: 各类型数据源格式化统一在源头闭环，防止逻辑过于分散，UI组件直接用于渲染即可
    const processItem = (answer) => {
      const { type, value } = answer || {};
      if (type === 'think') {
        return {
          ...answer,
          value: value?.map((task) => {
            if (
              task.type === 'think' ||
              task.type === 'think_tool' ||
              task.type === 'text'
            ) {
              return { ...task, value: preprocessMarkdown(task?.value) };
            }
            if (task.type === 'think_mode') {
              return { ...task, subType: 'think_mode' as const };
            }
            return task;
          }),

        };
      }
      if (type !== 'text') return answer;
      return { ...answer, value: preprocessMarkdown(value) };
    };

    return (data || []).map((conversation, index) => {
      const answers = conversation.answer || [];
      // 情况1：没有流式状态，说明是历史数据 -> 处理所有回答
      if (!streamingState) {
        return {
          ...conversation,
          answer: answers.map(processItem)
        };
      }
      // 情况2：有流式状态 -> 只处理最后一项的回答，n-1的数据直接返回，不做处理
      if (index === data.length - 1) {
        return {
          ...conversation,
          answer: answers.map(processItem)
        };
      }
      // 其他情况直接返回原数据
      return conversation;
    });
  }, [data, loading, streamingState]);
  const updateStreamingState = (
    state: Exclude<TStreamingState, null>,
  ) => {
    streamingStateRef.current = state;
  };
  // 总分类
  const currentMessageInfoRef = useLocalRef<TCurrentMessageInfo>(`__dba_chat_history_current_message_info_${routeSessionId}__`, '');
  // think子类
  const currentThinkRef = useRef<string>();
  // 状态更新
  const updateConversations = (messageId: string, updateData: TStreamUpdateData) => {
    mutate((prev: any) => {
      const { type, value, thinkSubType, thinkId, done, title, subType, toolCallId, latency } = updateData || {};
      if (!type) {
        return prev;
      }
      const messageList = [...(prev || [])];
      const currentMessage = messageList.pop();
      const { answer = [] } = currentMessage || {};
      if (!currentMessage || currentMessage?.messageId !== messageId) {
        return prev;
      }

      const currentMessageInfo = currentMessageInfoRef.current;
      // 如果是思考总时，不计入，直接提前返回， 
      // 原地 mutate 后需返回新数组引用，否则 React 可能跳过 re-render
      if (thinkSubType === 'think_time_seconds') {
        currentMessage.thinkTimeSeconds = value;
        return [...messageList, { ...currentMessage }];
      }

      if (type === 'think' && thinkSubType === 'tool_output') {
        const thinkContainer = answer.find((item: { type?: string; }) => item.type === 'think');
        if (Array.isArray(thinkContainer?.value)) {
          const toolUseItem = thinkContainer.value.find((item: { type?: string; thinkId?: string; tool_call_id?: string; }) =>
            item?.type === 'tool_use' &&
            item?.thinkId === thinkId &&
            item?.tool_call_id === toolCallId
          );
          if (toolUseItem) {
            const nextLatency = Number(latency) || 0;
            if (toolUseItem.latency === nextLatency) {
              return prev;
            }
            const toolUseIndex = thinkContainer.value.indexOf(toolUseItem);
            if (toolUseIndex !== -1) {
              thinkContainer.value = [
                ...thinkContainer.value.slice(0, toolUseIndex),
                { ...toolUseItem, latency: nextLatency },
                ...thinkContainer.value.slice(toolUseIndex + 1),
              ];
            }
          }
        }
        return [...messageList, { ...currentMessage }];
      }

      if (done) {
        const thinkItem = answer.find(item => item.type === 'think');
        if (thinkItem?.value && Array.isArray(thinkItem.value)) {
          const lastThinkId = thinkItem.value.find(item =>
            item?.type === 'switch_agent' && item?.thinkId === thinkId
          );
          if (lastThinkId) {
            if (lastThinkId.done === done) {
              return prev;
            }
            const thinkIdIndex = thinkItem.value.indexOf(lastThinkId);
            if (thinkIdIndex !== -1) {
              thinkItem.value = [
                ...thinkItem.value.slice(0, thinkIdIndex),
                { ...lastThinkId, done },
                ...thinkItem.value.slice(thinkIdIndex + 1),
              ];
            }
          }
          // 尾部 text 提取：think.value 最后一项是 text 则挪到 answer
          const lastItem = thinkItem.value[thinkItem.value.length - 1];
          if (lastItem?.type === 'text') {
            thinkItem.value = thinkItem.value.slice(0, -1);
            answer.push({ type: 'text', value: String(lastItem.value || '') });
          }
        }
        return [...messageList, { ...currentMessage, answer }];
      }

      if (type) {
        currentMessageInfoRef.current = type;
        currentThinkRef.current = thinkSubType;
      }

      // 如果上一条是think，则更新状态为success,代表思考过程结束
      if (currentMessageInfo === 'think' && type !== 'think') {  // (ESLint Fix #1)
        answer[answer.length - 1] = {
          ...answer[answer.length - 1],
          status: 'success'
        };
      }

      // 如果当前不是think，且流式状态为pending，则更新流式状态为progressing
      if (type !== 'think' && streamingStateRef?.current === 'pending') {
        updateStreamingState('progressing');
      }
      // TODO: 流失输出类型的，统一在一个分支内处理
      if (type === 'text') {
        // text处理
        if (currentMessageInfo !== 'text') {
          answer.push({ type, value });
        } else {
          if (answer[answer.length - 1].type === type) {
            answer[answer.length - 1].value += value;
          }
        }
      } else if ((TEXT_CONCAT_DATA_TYPES as readonly string[]).includes(type)) {
        // 处理data_description/complete_summary数据：按各自type独立整合，支持多个不同类型同时渲染
        // 解决后端在数据中插入其他类型数据导致value无法拼接的问题
        const existingItem = answer.find(item => item.type === type);
        if (existingItem) {
          // 如果已存在同类型数据，则拼接value
          existingItem.value += value;
        } else {
          // 如果不存在同类型数据，则新建一个
          answer.push({ type, value, title });
        }
      } else if ((CHART_DATA_TYPES as readonly string[]).includes(type)) {
        // 图表数据处理（含 echarts_option 流式数据，保持与 history 反序列化一致的对象形态）
        answer.push({ type, value });
      } else if (type === 'think') {
        let flag = true;
        if (isDsReport && thinkSubType !== 'think') {
          flag = answer.find(item => item.type === 'think') && false;
        }
        // 如果上一条不是think，则直接添加
        if (currentMessageInfo !== 'think' && flag) {
          // 如果answer没有think，直接添加，answer里面不允许存在2个think，只展示一个机器人思考
          const hasThink = answer.find(item => item.type === 'think');
          let newItem;
          if (thinkSubType === 'think_mode') {
            newItem = { type: thinkSubType, value, thinkId, subType: 'think_mode' as const };
          } else if (thinkSubType === 'think_tool') {
            newItem = { type: thinkSubType, value: isString(value) ? value : '', thinkId, tool_call_id: toolCallId };
          } else if (thinkSubType === 'text') {
            newItem = { type: 'text', value: isString(value) ? value : '', thinkId, tool_call_id: toolCallId };
          } else if (thinkSubType === 'think_fold_mode') {
            newItem = { type: 'think_fold_mode', value, thinkId };
          } else {
            const normalizedValue = isString(value) ? { value } : (isObject(value) ? value : {});
            newItem = { type: thinkSubType, ...normalizedValue, thinkId };
          }
          // 如果answer没有think，直接添加，answer里面不允许存在2个think，只展示一个机器人思考
          if (isEmpty(hasThink?.value) || isEmpty(hasThink)) {
            if (isEmpty(hasThink)) {
              answer.push({ type: 'think', value: [newItem], status: "pending" });
            } else {
              hasThink.value = [newItem];
            }
          } else if (currentMessageInfo !== 'text' && Array.isArray(hasThink?.value)) {
            // 正文开始后仍追加到已有 think.value，避免多余 think 块
            if (thinkSubType === 'think_mode' || thinkSubType === 'think_fold_mode') {
              hasThink.value.unshift(newItem);
            } else {
              hasThink.value.push(newItem);
            }
          }
        } else {
          // 获取最后一个think项
          let lastThinkItem = answer[answer.length - 1];
          if (isDsReport && thinkSubType === 'think') {
            lastThinkItem = answer.find(item => item.type === 'think');
          }
          // 兜底：当最后一项不是 think 容器时（如 text 插在 think 和 bq_chart 之间），向前查找实际的 think 容器
          if (lastThinkItem && !Array.isArray(lastThinkItem.value)) {
            lastThinkItem = answer.find(item => item.type === 'think');
          }
          // 这个算法待优化
          const lastValueItem = Array?.isArray(lastThinkItem?.value) && lastThinkItem?.value?.find(item => item?.type === 'think' && item?.thinkId === thinkId) || {};
          const existingThinkToolItem = Array?.isArray(lastThinkItem?.value)
            ? lastThinkItem?.value?.find(item =>
              item?.type === 'think_tool' &&
              item?.thinkId === thinkId &&
              item?.tool_call_id === toolCallId
            )
            : null;
          const existingThinkTextItem = Array?.isArray(lastThinkItem?.value)
            ? lastThinkItem?.value?.find(item =>
              item?.type === 'text' &&
              item?.thinkId === thinkId &&
              item?.tool_call_id === toolCallId
            )
            : null;

          // 如果最后一个元素的type与当前thinkSubType相同，则拼接value, 只允许think可以做拼接行为，其他类型数据都是结构化数据
          if (lastValueItem?.type === thinkSubType && lastValueItem?.type === 'think') {
            lastValueItem.value += (value || '');
          } else if (thinkSubType === 'think_tool' && existingThinkToolItem) {
            existingThinkToolItem.value += (value || '');
          } else if (thinkSubType === 'text' && toolCallId && existingThinkTextItem) {
            existingThinkTextItem.value += (value || '');
          } else if (thinkSubType === 'think_fold_mode') {
            if (Array.isArray(lastThinkItem?.value)) {
              const existingMode = lastThinkItem.value.find(
                (item: { type?: string; }) => item?.type === 'think_fold_mode',
              );
              if (existingMode) {
                const modeIndex = lastThinkItem.value.indexOf(existingMode);
                lastThinkItem.value = [
                  ...lastThinkItem.value.slice(0, modeIndex),
                  { ...existingMode, value },
                  ...lastThinkItem.value.slice(modeIndex + 1),
                ];
              } else {
                lastThinkItem.value = [
                  { type: 'think_fold_mode', value, thinkId },
                  ...lastThinkItem.value,
                ];
              }
            }
          } else {
            // 否则，添加一个新的元素到value数组
            let newItem;
            if (thinkSubType === 'switch_agent') {
              newItem = {
                type: thinkSubType,
                ...value,
                value: value?.input,
                thinkId,
              };
            } else if (thinkSubType === 'plan_json') {
              newItem = {
                buried: value,
                type: thinkSubType,
                value: parseJsonSafely(value)?.rephrase || thinkSubType,
                thinkId
              };
            } else if (thinkSubType === 'tool_use') {
              newItem = {
                type: thinkSubType,
                ...value,
                value: value?.value,
                thinkId
              };
            } else if (thinkSubType === 'think_tool') {
              newItem = {
                type: thinkSubType,
                value,
                thinkId,
                tool_call_id: toolCallId
              };
            } else if (thinkSubType === 'text') {
              newItem = {
                type: 'text',
                value,
                thinkId,
                tool_call_id: toolCallId
              };
            } else if (thinkSubType === 'think_mode') {
              newItem = { type: thinkSubType, value, thinkId, subType: 'think_mode' as const };
            } else {
              newItem = { type: thinkSubType, value, thinkId };
            }
            if (isDsReport && thinkSubType !== 'think') {
              lastThinkItem = answer.find(item => item.type === 'think');
            }
            // 如果是 think_mode，插入到数组首行
            if (thinkSubType === 'think_mode' && Array?.isArray(lastThinkItem?.value)) {
              lastThinkItem?.value?.unshift(newItem);
            } else {
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              Array?.isArray(lastThinkItem?.value) && lastThinkItem?.value?.push(newItem);
            }
          }

        }
      } else if (type === 'interfaceException') {
        // 处理error数据：只保留第一个error，后续的error直接忽略
        // 解决多个error导致文字重叠展示的问题
        const existingError = answer.find(item => item.type === 'error');
        if (!existingError) {
          // 只有不存在error数据时，才添加新的error
          answer.push({ type: 'error', value });
        }
        // 如果已存在error数据，直接忽略新的error
      } else if (type === 'sensitiveInfo') {
        // 经分敏感数据：仅保留第一条，后续同类数据忽略
        const existingSensitive = answer.find(item => item.type === 'sensitiveInfo');
        if (!existingSensitive) {
          const sensitiveItem = { type, value };
          const thinkIdx = answer.findIndex(item => item.type === 'think');
          answer.splice(thinkIdx + 1, 0, sensitiveItem);
        }
      } else if (type === 'costWarning') {
        // 额度超限提示：仅保留第一条，插入 think 后（由 ThinkCostWarningPortal 渲染）
        const existingWarning = answer.find(item => item.type === 'costWarning');
        if (!existingWarning) {
          const thinkIdx = answer.findIndex(item => item.type === 'think');
          answer.splice(thinkIdx + 1, 0, { type: 'costWarning', value });
        }
      } else if (type === 'opinion_report_card') {
        const cardsToAdd = compact(castArray(value));
        const existingIndex = answer.findIndex(
          item => item.type === 'opinion_report_card',
        );
        if (existingIndex >= 0) {
          answer[existingIndex] = {
            ...answer[existingIndex],
            value: compact(castArray(answer[existingIndex].value)).concat(cardsToAdd),
          };
        } else {
          answer.push({ type, value: cardsToAdd });
        }
      } else if (type === 'document') {
        // 文档类型（document/ai_excel_file/magic_poster_file 统一为 document）
        // subType 保存真实类型用于组件映射
        answer.push({ type, value, ...(subType && { subType }) });
      } else if (type === 'currentTokenTotal') {
        const thinkItem = answer.find(item => item.type === 'think');
        if (thinkItem && Array.isArray(thinkItem.value)) {
          thinkItem.value.push({
            type: 'current_token_total',
            value: Number(value) || 0,
          });
        }
      } else if (type === 'reference') {
        // 处理reference数据：将所有reference数据统一整合到一个位置
        // 解决后端在references数据中插入其他类型数据导致前端展示异常的问题
        const existingReference = answer.find(item => item.type === 'reference');
        if (existingReference) {
          // 如果已存在reference数据，则将新数据合并到现有数组中
          existingReference.value.push(value);
        } else {
          // 如果不存在reference数据，则新建一个
          answer.push({ type, value: [value] });
        }
      } else if (type === 'database_query') {
        // 多段 SSE：后续段 concat 进已有 database_query.value
        // splice 插入到思考过程之后（参照 costWarning 模式）
        const existingItem = find(answer, { type: 'database_query' });
        const list = castArray(value);
        if (existingItem) {
          existingItem.value = concat(existingItem.value, list);
        } else {
          const thinkIdx = answer.findIndex(item => item.type === 'think');
          answer.splice(thinkIdx + 1, 0, { type, value: list });
        }
      } else {
        if (currentMessageInfo !== type) {
          answer.push({ type, value: [value] });
        } else {
          answer[answer.length - 1].value.push(value);
        }
      }
      return [...messageList, { ...currentMessage, answer }];
    });
  };

  // 错误提示，内部维护
  const errorTipsMessage = useCallback((key: keyof typeof ERROR_CODES | string) => {
    const messages = {
      [ERROR_CODES.PARSE_ERROR]: 'dba_ParseError',
      [ERROR_CODES.INVALID_REQUEST]: 'dba_invalid_request',
      [ERROR_CODES.METHOD_NOT_FOUND]: 'dba_method_notFound',
      [ERROR_CODES.INVALID_PARAMS]: 'dba_invalid_params',
      [ERROR_CODES.INTERNAL_ERROR]: 'dba_internal_error',
      [ERROR_CODES.MAX_TURNS]: 'dba_max_turns',
      [ERROR_CODES.INVALID_AGENT]: 'dba_invalid_agent',
      [ERROR_CODES.INVALID_IMAGE]: 'dba_invalid_image',
      [ERROR_CODES.OVER_TOKEN]: 'dba_over_token',
      [ERROR_CODES.RATE_LIMIT]: 'dba_rate_limit',
      [ERROR_CODES.REQUEST_LIMIT]: 'dba_request_limit',
      [ERROR_CODES.EXECUTION_TIMEOUT]: 'dba_execution_timeout',
      [ERROR_CODES.INVALID_INPUT]: 'dba_invalid_input',
    };
    const value = messages[key];
    return value ? formatMessage({ id: value }) : '';
  }, [locale]);

  // 创建新对话(单个qa)
  const startNewConversation = async (
    queryValue: string,
    singleMessageFileList: any,
    options?: {
      onOpen?: () => void;
      onSuccess?: () => void;
      onError?: () => void;
      onComplete?: () => void;
      onBefore?: (...args: any) => void;
    },
    otherParams?: {
      from?: 'edit' | 'refresh' | 'send' | 'recommend' | 'homeQuestion';
      outConversationsList: Conversation[];
      logFrom?: string;
      serverParams?: any;
      customQuestion?: boolean;
      logParams?: {[key: string]: any;};
      fileListData?: any[];
      targetSessionId?: string;
      onInsertConversation?: (params: {
        conversation: Conversation;
        targetSessionId: string;
      }) => void;
    }
  ) => {
    const { onSuccess, onError, onOpen, onComplete, onBefore } = options || {};
    const { from, outConversationsList, serverParams, logFrom, customQuestion = false, logParams, fileListData, targetSessionId, onInsertConversation } = otherParams || {};
    const effectiveSessionId = targetSessionId || routeSessionId;
    const id = uuidv4();
    const userOpenId = userInfo?.openid;
    const { mode, model_id, system_language } = serverParams || {};
    // 从 serverParams 中剥离 frontend_metadata，放到 params 顶层
    const { frontend_metadata: fmParam, ...restServerParams } = serverParams || {};
    // 保存 options 到 useNetworkRecovery 内部的 ref，用于断网重连时使用
    setOptions({
      onSuccess,
      onError,
      onComplete,
      session_id: effectiveSessionId,
      message_id: id,
    });

    // 发送和编辑为singleMessageFileList，没有发送和编辑，则是从首页跳转过来用initFileList，其他情况为空[]
    const filetList = singleMessageFileList?.length > 0 ? singleMessageFileList : initFileList;
    const newConversation: Conversation = {
      messageId: id,
      query: (fmParam as { display_query?: string; } | undefined)?.display_query || queryValue,
      mode,
      modeId: model_id,
      frontFileList: filetList || [],
      customQuestion,
      metadata: {...restServerParams || {}},
      // 调整动机: 前端预插入一段think, 用于用户发送对话时，秒出机器人思考文本，防止等待，提高用户体验性
      answer: [
        {
          "type": "think",
          "value": [],
          "status": "pending"
        }
      ],
      system_language,
      frontend_metadata: fmParam
    };
  
    onBefore?.(id);
    resetStreamingState();
    // updateAiThinkIngState('pending');
    if (from === 'edit' || from === 'refresh') {
      // 如果是编辑，则前端手动模拟删除数据（outConversationsList），然后添加新的问题
      setConversations([...(outConversationsList || []), newConversation,]);
    } else if (isFunction(onInsertConversation)) {
      // 外部处理插入：仅暴露 conversation 和 targetSessionId，由外部通过全局状态自行处理
      onInsertConversation({ conversation: newConversation, targetSessionId: effectiveSessionId });
    } else {
      setConversations([...(conversations || []), newConversation]);
    }
   
    const requestData = {
      method: 'tasks/sendSubscribe',
      jsonrpc: '2.0',
      params: {
        id,
        sessionId: effectiveSessionId,
        message: {
          role: 'user',
          parts: [
            { type: 'text', text: queryValue },
            ...(fileListData || [])
          ],
        },
        // 暂时的测试userId
        metadata: { 
          userId: userOpenId,
          ...restServerParams 
        },
        ...(fmParam && { frontend_metadata: fmParam }),
      },
    };
    buriedBtnPoint({
      pageId: '700501',
      buttonId: '700501001',
      extInfo: JSON.stringify({
        sessionId: effectiveSessionId,
        message: queryValue,
        mode,
        model_id,
        from: logFrom || from,
        ...(logParams ? { ...logParams } : {})
      }),
      typeId: 'aigc',
    });
    const apiUrl = chatMode === 'deepseek' && (template && template !== 'dsReport')
      ? '/api/agent/chat_ds' 
      : '/api/agent/chat';

    // 发起 SSE 连接
    await fetchEventSource(apiUrl, {
      method: 'POST', // 支持 POST
      openWhenHidden: true,  // 强制保持连接
      headers: {
        'Authorization': getAuthorization() || '', // 自定义请求头
      },
      body: JSON.stringify(requestData), // 请求体
      onopen: async (response) => {
        // 非 2xx 直接中止，避免自动重试复用同一请求体
        if (!response.ok) {
          throw new Error(`SSE connection failed with status ${response.status}`);
        }
        onOpen?.();
        console.log('连接成功', response.status);
      },
      onmessage(rawChunk) {
        // console.log('收到数据:', rawChunk); // 处理服务器推送的数据
        processStream(rawChunk, id);
        onSuccess?.();
      },
      onclose() {
        console.log('连接关闭');
        //  更新流式状态为完成
        updateStreamingState('complete');
        // 使用mutate更新最后一条对话的状态，解决动机: 当还在ai思考过程时，直接点击stop，或者后端数据只返回think数据，会导致ai思考一直loading
        mutate((prevConversations) => {
          if (!prevConversations || prevConversations?.length === 0) {
            return prevConversations;
          }

          const lastConversation = prevConversations[prevConversations.length - 1];
          if (!lastConversation.answer || lastConversation?.answer?.length === 0) {
            return prevConversations;
          }

          const answer = [...lastConversation.answer];

          // 尾部 text 提取：think.value 最后一项是 text 则挪到 answer
          const thinkItem = answer.find(item => item.type === 'think' && Array.isArray(item.value));
          if (thinkItem && thinkItem.value.length > 0) {
            const lastItem = thinkItem.value[thinkItem.value.length - 1];
            if (lastItem?.type === 'text') {
              thinkItem.value = thinkItem.value.slice(0, -1);
              answer.push({ type: 'text', value: String(lastItem.value || '') });
            }
          }

          // 标记 think 为 success
          const updatedAnswer = answer.map((item) =>
            item.type === 'think'
              ? { ...item, status: 'success' }
              : item
          );
          return [
            ...prevConversations.slice(0, -1),
            { ...lastConversation, answer: updatedAnswer }
          ];
        });
        // 执行外部传入的onComplete回调函数
        onComplete?.();
      },
      onerror(error) {
        console.error('发生错误:', error);
        console.log('error:~onError', error);
        // 清理 streamingStateRef，避免状态不一致 (Code Review Fix #7)
        streamingStateRef.current = 'idle';
        // 再处理离线事件
        handleOfflineEvent(error?.message, streamingStateRef.current);
        onError?.(error);
        buriedBtnPoint({
          pageId: '700501',
          buttonId: '700501021',
          extInfo: JSON.stringify({
            sessionId,
            from: '流式异常错误',
            value: error,
            msdId: id
          }),
          typeId: 'aigc',
        });
        // 抛出错误，阻止 @microsoft/fetch-event-source 自动重试
        throw error;
      },
    });
  };

  // 流式处理方法
  const processStream = (rawChunk: IStreamChunk, messageId: string) => {
    if (!rawChunk || !isObject(rawChunk)) {
      console.error('Invalid input: rawChunk must be an object');
      return;
    }
    // SSE 心跳/空帧（rawChunk.data 为空或非字符串）直接跳过：
    // 否则 JSON.parse('') 会抛 "Unexpected end of JSON input"，刷屏报错且干扰排查（海报生成场景表现为不稳定）。
    const rawData = rawChunk?.data;
    if (!isString(rawData) || rawData.trim() === '') {
      return;
    }
    let data: any;
    try {
      data = JSON.parse(rawData);
    } catch (error) {
      // 分片/非法 JSON 帧：忽略单帧，避免影响整条流式处理
      console.warn('processStream: 跳过无法解析的流式帧', error);
      return;
    }
    try {
      if (data?.error) {
        updateConversations(messageId, { type: 'interfaceException', value: data?.error });
        buriedBtnPoint({
          pageId: '700501',
          buttonId: '700501020',
          extInfo: JSON.stringify({
            sessionId,
            from: '流式输出错误',
            value: data?.error,
            msdId: messageId
          }),
          typeId: 'aigc',
        });
      }
      if (data?.result?.artifact) {
        const parts = data.result.artifact.parts || [];
        // 并发id
        const thinkId = data?.result?.id;
        // 并发状态
        const done = data.result.artifact?.lastChunk;
        if (done) {
          updateConversations(messageId, { type: 'think', value: '', thinkSubType: 'think', thinkId, done });
        }
        parts.forEach((part: MessagePart) => {
          if (part.type === 'text' && part.text) {
            if (part.metadata?.tool_call_id) {
              updateConversations(messageId, {
                type: 'think',
                value: part.text,
                thinkSubType: 'text',
                thinkId,
                toolCallId: part.metadata.tool_call_id,
              });
            } else if (!done && thinkId && part.metadata?.id) {
              updateConversations(messageId, {
                type: 'think',
                value: part.text,
                thinkSubType: 'text',
                thinkId,
              });
            } else {
              updateConversations(messageId, { type: 'text', value: part.text });
            }
          } else if (part.type === 'data') {
            const { type: dataType, value: dataValue } = part.data || {};
            switch (dataType) {
              case 'switch_agent':
              // TODO: 待调整
                (part?.data?.new_agent !== 'Planner Agent' && part?.data?.new_agent !== 'Summary Agent') && updateConversations(messageId, { type: 'think', value: part.data, thinkSubType: dataType, thinkId });
                break;
              case 'tool_use':
                updateConversations(messageId, { type: 'think', value: part.data, thinkSubType: dataType, thinkId });
                break;
              case 'think':
                updateConversations(messageId, { type: 'think', value: part.data?.value, thinkSubType: dataType, thinkId });
                break;
              case 'think_tool':
                updateConversations(messageId, {
                  type: 'think',
                  value: part.data?.value || '',
                  thinkSubType: dataType,
                  thinkId,
                  toolCallId: part?.data?.tool_call_id || ''
                });
                break;
              case 'tool_output':
                updateConversations(messageId, {
                  type: 'think',
                  thinkSubType: 'tool_output',
                  thinkId,
                  toolCallId: part?.data?.tool_call_id || '',
                  latency: part?.data?.latency,
                  value: '',
                });
                break;

              case 'think_fold_mode':
                updateConversations(messageId, {
                  type: 'think',
                  thinkSubType: 'think_fold_mode',
                  value: part.data?.value,
                  thinkId,
                });
                break;
              case 'plan_json':
                updateConversations(messageId, { type: 'think', value: part.data?.value, thinkSubType: dataType, thinkId });
                break;
              case 'think_time_seconds':
                updateConversations(messageId, { type: 'think', value: part.data?.value, thinkSubType: dataType, thinkId });
                break;
              case 'reference': {
                const refData = parseJsonSafely(dataValue);
                updateConversations(messageId, { type: 'reference', value: refData });
                break;
              }
              case 'database_query': {
                const refData = parseJsonSafely(dataValue);
                updateConversations(messageId, { type: 'database_query', value: refData });
                break;
              }
              case 'bi_data': {
                const refData = parseJsonSafely(dataValue);
                updateConversations(messageId, { type: 'biData', value: refData });
                break;
              }
              case 'echarts_option': {
                const refData = parseJsonSafely(dataValue);
                updateConversations(messageId, { type: 'echartsOption', value: refData });
                break;
              }
              case 'bq_chart': {
                const refData = parseJsonSafely(dataValue);
                if ((part.data as any)?.data_type === 'think') {
                  // 多agent场景：嵌入 think 块，复用 think 路由逻辑
                  updateConversations(messageId, { type: 'think', value: refData, thinkSubType: 'bq_chart', thinkId });
                } else {
                  // 单agent场景：渲染在正文（原有行为）
                  updateConversations(messageId, { type: 'bq_chart', value: refData });
                }
                break;
              }
              case 'sensitive_data_info': {
                const sensitiveData = parseJsonSafely(dataValue);
                updateConversations(messageId, { type: 'sensitiveInfo', value: sensitiveData });
                break;
              }
              case 'warning': {
                const warningData = parseJsonSafely(dataValue);
                updateConversations(messageId, { type: 'costWarning', value: warningData });
                break;
              }
              // 文档类统一以 type:'document' 渲染 DocumentCmp，通过 subType 透传真实类型用于组件映射
              case 'document':
              case 'ai_excel_file':
              case 'magic_poster_file': {
                const docValue = parseJsonSafely(part.data?.value);
                updateConversations(messageId, {
                  type: 'document',
                  value: docValue,
                  subType: dataType,
                });
                break;
              }
              case 'think_mode':
                updateConversations(messageId, { type: 'think', value: part.data?.value, thinkSubType: dataType, thinkId });
                break;
              case 'complete_summary':
              case 'data_description': {
                updateConversations(messageId, {
                  type: dataType,
                  title: part?.data?.title || '',
                  value: dataValue || '',
                });
                break;
              }
              case 'current_token_total':
                updateConversations(messageId, {
                  type: 'currentTokenTotal',
                  value: part.data?.value,
                });
                break;
              case 'opinion_report_card':
                updateConversations(messageId, { type: 'opinion_report_card', value: parseJsonSafely(part.data?.value) });
                break;
              default:
                if (process.env.NODE_ENV !== 'production') {
                  // 后端新增类型时前端应能感知并补处理，避免静默吞数据
                  console.warn('[useChatHistory] unhandled stream dataType:', dataType);
                }
                break;
            }
          }
        });};
    } catch (error) {
      console.error('processStream处理失败:', error);
    }
  };
  
  // 重置流式状态信息
  const resetStreamingState = () => {
    updateStreamingState('pending');
    currentMessageInfoRef.current = '';
    currentThinkRef.current = '';
  };
        
  // 网络断开/断网恢复、SSE主动终端
  const { setOptions, handleOfflineEvent, retrying } = useNetworkRecovery({mutate, processStream, updateStreamingState, resetStreamingState, conversations, streamingState});

  // 取消请求
  const cancelRequest = () => {
    mutate((prevConversations) => {
      if (!prevConversations || prevConversations.length === 0) {
        return prevConversations;
      }
      const lastConversation = prevConversations[prevConversations.length - 1];
      if (!lastConversation.answer) {
        lastConversation.answer = [];
      }
      const answer = lastConversation.answer;

      // 尾部 text 提取：think.value 最后一项是 text 则挪到 answer
      const thinkItem = answer.find(item => item.type === 'think' && Array.isArray(item.value));
      if (thinkItem && thinkItem.value.length > 0) {
        const lastItem = thinkItem.value[thinkItem.value.length - 1];
        if (lastItem?.type === 'text') {
          thinkItem.value = thinkItem.value.slice(0, -1);
          answer.push({ type: 'text', value: String(lastItem.value || '') });
        }
      }

      answer.push({ type: 'error', value: 'stop' });
      return [...prevConversations];
    });
  };

  // 更新全局sessionId
  const handleUpdateSessionId = useMemoizedFn((id?: string) => {
    setSessionCopilotId(id || uuidv4());
  });

  // 清空全局sessionId
  const handleClearSessionId = useMemoizedFn(() => {
    setSessionCopilotId('');
  });
  
  // 更新全局 dsReport（底层按 document_id 存储）
  const handleUpdateDsReport = useMemoizedFn((params) => {
    // 自动补上 sourceSessionId，确保 ChatSmartTable / MagicPosterPanel 在 CopilotReportWindow
    // 中渲染时能拿到原始会话 ID 来调用 API
    const enrichedParams = {
      ...params,
      sourceSessionId: params?.sourceSessionId || routeSessionId,
    };
    // 当 show: true 且有 document_id 时，调用 openReport 打开报告
    if (enrichedParams?.show === true && (enrichedParams?.document_id || enrichedParams?.id)) {
      openReport(enrichedParams);
      store.setItem(COPILOT_REPORT_SESSION_ID_KEY, routeSessionId);
    } else {
      updateReport(enrichedParams);
    }
  });

  // 点赞点踩缓存数据更新
  const handleLikeOrDislike = useMemoizedFn((messageId: string, like: number) => {
    mutate((prevConversations) => {
      if (!prevConversations || prevConversations.length === 0) {
        return prevConversations;
      }
      const matchItem = prevConversations.find((item) => item.messageId === messageId);
      if (matchItem) {
        matchItem.like = like;
      }
      return [...prevConversations];
    });
  });

  return {
    conversations,
    streamingState,
    startNewConversation,
    setConversations,
    updateStreamingState,
    cancelRequest,
    fetchHistoryList,
    userInfo,
    errorTipsMessage, 
    handleUpdateSessionId,
    handleClearSessionId,
    sessionCopilotId,
    handleLikeOrDislike,
    dsReport,
    handleUpdateDsReport,
    loading,
    retrying,
  };
};
export default useChatHistory;
